import React, {useCallback, useEffect, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import "./landing.css";
import {MAINTENANCE_CONTACT_EMAIL, MAINTENANCE_MODE} from "../../feature_flags";
import {MaintenanceModal} from "../../components/MaintenanceNotice";
import Logo from "../../components/Logo/Logo";

// ─── Video config ────────────────────────────────────────────────────────────
const GCS_BASE = "https://storage.googleapis.com/loa-videos";
const FULL_DEMO_URL = `${GCS_BASE}/Full Demo.mp4`;
const CALENDLY_URL = "https://calendly.com/ceyer/ai-document-review-exploration-with-loa?hide_event_type_details=1&hide_gdpr_banner=1";

type Demo = {id: string; title: string; description: string; videoFile: string};

// ─── Content ─────────────────────────────────────────────────────────────────
const steps = [
    {
        title: "Drop in the whole pile",
        description: "PDFs, scanned exhibits, emails, transcripts, ZIP archives. Thousands of pages at once. OCR and parsing run on upload, with no sorting or renaming first.",
    },
    {
        title: "LoA organizes it",
        description: "Every document is summarized, foldered, and tagged. People, companies, and events are extracted with their legal roles, and every page is indexed for full-text search.",
    },
    {
        title: "Walk in knowing the file",
        description: "Ask questions across the corpus. Surface contradictions with verbatim quotes and page cites. Find any document by what is in it, not where it was filed.",
    },
];

const demoSections: {id: string; label: string; description: string; demos: Demo[]}[] = [
    {
        id: "ingest",
        label: "Upload and extract",
        description: "Drag-and-drop ingestion with automatic parsing, OCR, and entity extraction.",
        demos: [
            {id: "secure-upload", title: "Bulk upload", description: "Drop ZIP archives or PDFs. Progress tracking for large batches.", videoFile: "Bulk upload.mp4"},
            {id: "entity-extraction", title: "Entity extraction", description: "People, companies, and events identified with their legal roles.", videoFile: "Entity Extraction.mp4"},
        ],
    },
    {
        id: "transcripts",
        label: "Transcript analysis",
        description: "Deposition and hearing transcripts parsed into examination blocks, with a credibility profile per witness.",
        demos: [
            {id: "cross-reference", title: "Cross-examination insights", description: "Admissions, denials, key facts, and objections per examination block. Click any exchange to jump to the quote in the transcript.", videoFile: "Cross Examination Insight.mp4"},
            {id: "contradiction-chat", title: "Contradictions on demand", description: "Ask where a witness contradicts themselves, in one deposition or across every transcript in the case. Each hit comes with verbatim quotes and page citations.", videoFile: "Find Contradictions.mp4"},
            {id: "witness-credibility", title: "Witness credibility", description: "A witness's full record across every deposition: admissions, denials, and inconsistencies in one profile.", videoFile: "Witness Credibility.mp4"},
        ],
    },
    {
        id: "review",
        label: "Review",
        description: "Define your coding scheme once. LoA applies it across the corpus with cited evidence for every decision.",
        demos: [
            {id: "define-codes", title: "Codes", description: "Tags with natural-language definitions. LoA expands your intent, then applies each tag with verbatim quotes as evidence.", videoFile: "Codes.mp4"},
            {id: "document-requests", title: "Document requests", description: "Define formal requests and evaluate responsiveness automatically, each decision backed by cited evidence.", videoFile: "Document Requests.mp4"},
            {id: "contextual-chat", title: "Chat with @context", description: "@mention folders, tags, or entities to focus the conversation.", videoFile: "Chat With Context.mp4"},
        ],
    },
    {
        id: "act",
        label: "Search and export",
        description: "Advanced filtering, Bates stamping, and production-ready exports.",
        demos: [
            {id: "advanced-filters", title: "Saved searches", description: "Filters with regex, dates, and tag scores. Save and reuse.", videoFile: "Saved Searches.mp4"},
            {id: "production", title: "Productions", description: "Select by tag, Bates stamp with smart placement, export.", videoFile: "Productions.mp4"},
        ],
    },
];

const capabilities = [
    {
        title: "Evidence-backed tagging",
        description: "Every AI-generated tag includes verbatim quotes from the source. Your team sees exactly why a document was flagged, which gives you an auditable trail for every decision.",
    },
    {
        title: "Contextual chat",
        description: "Ask questions across the corpus. @mention folders, tags, or extracted entities to give the model focused context.",
    },
    {
        title: "Transcript analysis",
        description: "Depositions and hearing transcripts are parsed into examination blocks. LoA extracts admissions and denials, flags inconsistencies, and builds a credibility profile per witness.",
    },
    {
        title: "Document request evaluation",
        description: "Define formal document requests and let LoA evaluate responsiveness across the corpus, with relevance levels and cited evidence for each assessment.",
    },
];

const security = [
    {title: "Zero retention by the model", description: "Runs on Google Vertex AI. Your documents are never stored by the model and never used for training."},
    {title: "US-only, private network", description: "All processing in the US. Database and vector search run on a private VPC with no public exposure."},
    {title: "Encrypted at rest and in transit", description: "AES-256 on Cloud SQL and GCS. TLS everywhere. Nothing leaves Google Cloud."},
];

const faq = [
    {
        q: "Can LoA organize a large document dump quickly?",
        a: "Yes, that is the core workflow. Drop an entire discovery production, an inherited case file, or a client's unsorted PDFs and ZIP archives in at once. LoA runs OCR and parsing automatically, then summarizes, folders, and tags every document and indexes it for full-text search. A raw pile becomes a searchable case file in minutes instead of days of manual review.",
    },
    {
        q: "What types of documents does LoA support?",
        a: "PDFs, deposition transcripts (TXT/PTX), emails (EML, MSG, MBOX, PST), and ZIP archives. The pipeline handles OCR for scanned documents, entity extraction, and full-text indexing automatically on upload.",
    },
    {
        q: "How does the legal transcript analysis work?",
        a: "Upload deposition or hearing transcripts and LoA parses them into structured examination blocks. Each block is analyzed for admissions, denials, key facts, and objections. The system builds per-witness credibility profiles and can detect contradictions across multiple transcripts in the same case.",
    },
    {
        q: "How does automated coding differ from traditional e-discovery review?",
        a: "Traditional review tools require manual tagging or simple keyword rules. LoA lets you define tags with natural-language descriptions, then applies them across the corpus with cited evidence: verbatim quotes from the source explaining why each coding decision was made.",
    },
    {
        q: "Is my data secure?",
        a: "All processing runs on Google Cloud's US-based infrastructure within a private VPC. Documents are encrypted at rest with AES-256 (Cloud SQL and GCS) and in transit via TLS. LoA uses Vertex AI for language model processing. Your documents are never stored by the model and never used for training.",
    },
    {
        q: "Can LoA evaluate responsiveness to document requests?",
        a: "Yes. Define formal document requests and LoA evaluates responsiveness across the corpus. Each assessment includes relevance scoring and cited evidence from the source documents, which makes privilege log preparation and production decisions faster and more defensible.",
    },
    {
        q: "What export formats are available for productions?",
        a: "Production exports with configurable Bates stamping (in the filename, on the page, or both), ZIP archives, and Bates-mapping CSVs. Select documents by tag score, apply your numbering scheme, and export production-ready files.",
    },
];

// ─── Small pieces ────────────────────────────────────────────────────────────
const SectionLabel: React.FC<{n: string; children: React.ReactNode}> = ({n, children}) => (
    <p className="lp-mono flex items-center gap-3">
        <span>{n}</span>
        <span className="h-px w-6 bg-[var(--line)]"/>
        <span>{children}</span>
    </p>
);

const Wordmark: React.FC = () => (
    <a href="/" className="flex items-center gap-2 text-[var(--ink)]" aria-label="LoA home">
        <Logo sizePx={34} className="shrink-0"/>
        <span className="lp-serif text-[26px] leading-none tracking-tight">LoA</span>
    </a>
);

function DemoTile({demo, onExpand}: {demo: Demo; onExpand: () => void}) {
    const [hasError, setHasError] = useState(false);
    return (
        <button
            type="button"
            onClick={onExpand}
            className="group text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ink)] rounded-[12px]"
            aria-label={`Expand demo: ${demo.title}`}
        >
            <div className="lp-frame aspect-video relative">
                {!hasError ? (
                    <video
                        src={`${GCS_BASE}/${demo.videoFile}`}
                        className="w-full h-full object-cover"
                        muted loop playsInline autoPlay preload="metadata"
                        onError={() => setHasError(true)}
                    />
                ) : (
                    <div className="flex items-center justify-center h-full text-[13px] text-[#8b887f]">Demo coming soon</div>
                )}
                <div className="absolute bottom-3 right-3 lp-mono !text-white/80 bg-black/50 backdrop-blur px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                    Expand
                </div>
            </div>
            <h4 className="mt-4 text-[17px] font-medium leading-snug">{demo.title}</h4>
            <p className="lp-small mt-1.5">{demo.description}</p>
        </button>
    );
}

function DemoLightbox({demo, onClose}: {demo: Demo; onClose: () => void}) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("keydown", handler); document.body.style.overflow = prev; };
    }, [onClose]);

    return (
        <motion.div
            initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} transition={{duration: 0.18}}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div className="absolute inset-0 bg-[rgba(15,15,14,0.82)]"/>
            <motion.div
                initial={{scale: 0.97, opacity: 0}} animate={{scale: 1, opacity: 1}} exit={{scale: 0.97, opacity: 0}}
                transition={{duration: 0.2, ease: [0.2, 0.8, 0.2, 1]}}
                className="relative w-full max-w-[1200px]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-3 text-white">
                    <div>
                        <h3 className="text-[16px] font-medium">{demo.title}</h3>
                        <p className="text-[13px] text-white/60">{demo.description}</p>
                    </div>
                    <button onClick={onClose} className="lp-mono !text-white/70 hover:!text-white transition shrink-0 ml-6">Close · Esc</button>
                </div>
                <div className="rounded-[12px] overflow-hidden bg-black">
                    <video src={`${GCS_BASE}/${demo.videoFile}`} className="w-full max-h-[80vh] object-contain" autoPlay loop muted playsInline/>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function LandingPage() {
    const [expandedDemo, setExpandedDemo] = useState<Demo | null>(null);
    const [showMaintenance, setShowMaintenance] = useState(false);
    const closeMaintenance = useCallback(() => setShowMaintenance(false), []);

    // Every "get access" CTA is a real link to the app. In maintenance mode we
    // intercept the click and show the notice instead, and the label reads
    // "Request access" rather than inviting people to try an app that is down.
    const accessLabel = MAINTENANCE_MODE ? "Request access" : "Try it out";
    const onAccessClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (MAINTENANCE_MODE) {
            e.preventDefault();
            setShowMaintenance(true);
        }
    };

    return (
        <main className="lp bg-[var(--paper)] min-h-screen overflow-x-hidden">

            {/* ── Nav ── */}
            <header className="sticky top-0 z-30 bg-[rgba(251,250,247,0.85)] backdrop-blur border-b border-[var(--line)]">
                <div className="mx-auto max-w-[1200px] px-6 sm:px-10 h-[64px] flex items-center justify-between">
                    <Wordmark/>
                    <nav className="hidden md:flex items-center gap-8 text-[14px] text-[var(--ink-2)]" aria-label="Main navigation">
                        <a href="#product" className="hover:text-[var(--ink)] transition">Product</a>
                        <a href="#security" className="hover:text-[var(--ink)] transition">Security</a>
                        <a href="#faq" className="hover:text-[var(--ink)] transition">FAQ</a>
                        <a href="#contact" className="hover:text-[var(--ink)] transition">Contact</a>
                    </nav>
                    <a href="/app/" onClick={onAccessClick} className="lp-btn lp-btn-primary lp-btn-sm">
                        {accessLabel}
                    </a>
                </div>
            </header>

            {/* ── Hero ── */}
            <section className="mx-auto max-w-[1200px] px-6 sm:px-10 pt-20 sm:pt-28 pb-12">
                <motion.div
                    initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.5, ease: [0.2, 0.8, 0.2, 1]}}
                    className="max-w-[900px]"
                >
                    <p className="lp-mono">Legal transcript and document analysis AI</p>
                    <h1 className="lp-serif lp-h1 mt-6">
                        Read the whole file.<br/>
                        <span className="italic text-[var(--ink-2)]">Cite every page.</span>
                    </h1>
                    <p className="lp-lead mt-8 max-w-[640px]">
                        LoA takes a discovery production, an inherited case file, or a shoebox of scanned
                        PDFs and turns it into a summarized, tagged, searchable case file. Ask it a question
                        and get the answer with the quote and the page it came from.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-3">
                        <a href="/app/" onClick={onAccessClick} className="lp-btn lp-btn-primary">
                            {accessLabel}
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
                            </svg>
                        </a>
                        <a href="#demo" className="lp-btn lp-btn-ghost">Watch the demo</a>
                    </div>
                </motion.div>

                {/* Full walkthrough, always visible */}
                <div id="demo" className="mt-20 scroll-mt-24">
                    <div className="lp-frame aspect-video">
                        <video src={FULL_DEMO_URL} className="w-full h-full" controls playsInline preload="metadata" controlsList="nodownload"/>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                        <p className="lp-small">Full walkthrough: upload, analysis, coding, export.</p>
                    </div>
                </div>
            </section>

            {/* ── Facts strip ── */}
            <section className="mx-auto max-w-[1200px] px-6 sm:px-10 py-10">
                <dl className="grid sm:grid-cols-3 border-y border-[var(--line)]">
                    {[
                        ["Formats", "PDF, PTX, TXT, EML, MSG, MBOX, PST, ZIP"],
                        ["Model", "Google Vertex AI, zero data retention"],
                        ["Hosting", "US-only, private VPC on Google Cloud"],
                    ].map(([k, v], i) => (
                        <div key={k} className={`py-5 sm:pr-8 ${i > 0 ? "sm:pl-8 sm:border-l border-t sm:border-t-0 border-[var(--line)]" : ""}`}>
                            <dt className="lp-mono">{k}</dt>
                            <dd className="mt-2 text-[15px] leading-snug">{v}</dd>
                        </div>
                    ))}
                </dl>
            </section>

            {/* ── 01 When the dump lands ── */}
            <section className="mx-auto max-w-[1200px] px-6 sm:px-10 py-24 sm:py-32">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-28">
                            <SectionLabel n="01">When the dump lands</SectionLabel>
                            <h2 className="lp-serif lp-h2 mt-6">
                                A production drops. The deposition is Thursday. You haven&apos;t read any of it.
                            </h2>
                            <p className="lp-body mt-6 max-w-[460px]">
                                Reading a fresh dump by hand is nights and weekends you don&apos;t have.
                                Drop the whole thing into LoA and get a case file you can work in the
                                time it takes to get coffee.
                            </p>
                            <a href="/app/" onClick={onAccessClick} className="lp-link inline-block mt-8 text-[15px]">
                                Drop your first batch
                            </a>
                        </div>
                    </div>
                    <ol className="lg:col-span-7 border-t border-[var(--line)]">
                        {steps.map((s, i) => (
                            <li key={s.title} className="grid grid-cols-[48px_1fr] gap-4 py-8 border-b border-[var(--line)]">
                                <span className="lp-mono pt-1.5">0{i + 1}</span>
                                <div>
                                    <h3 className="lp-h3">{s.title}</h3>
                                    <p className="lp-body mt-3 max-w-[560px]">{s.description}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* ── 02 Product ── */}
            <section id="product" className="border-t border-[var(--line)] bg-[var(--paper-2)] scroll-mt-16">
                <div className="mx-auto max-w-[1200px] px-6 sm:px-10 py-24 sm:py-32">
                    <div className="max-w-[720px]">
                        <SectionLabel n="02">Product</SectionLabel>
                        <h2 className="lp-serif lp-h2 mt-6">From the dump to the deep dive.</h2>
                        <p className="lp-lead mt-6">
                            Getting organized is the start. The same corpus carries through transcript analysis,
                            evidence-backed coding, and production-ready exports.
                        </p>
                    </div>

                    <div className="mt-20 space-y-24">
                        {demoSections.map((section, i) => (
                            <div key={section.id} className="grid lg:grid-cols-12 gap-8 lg:gap-16">
                                <div className="lg:col-span-3">
                                    <div className="lg:sticky lg:top-28">
                                        <p className="lp-mono">{String(i + 1).padStart(2, "0")}</p>
                                        <h3 className="lp-h3 mt-2">{section.label}</h3>
                                        <p className="lp-small mt-3">{section.description}</p>
                                    </div>
                                </div>
                                <div className={`lg:col-span-9 grid gap-8 sm:grid-cols-2 ${section.demos.length === 3 ? "xl:grid-cols-3" : ""}`}>
                                    {section.demos.map((demo) => (
                                        <DemoTile key={demo.id} demo={demo} onExpand={() => setExpandedDemo(demo)}/>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── 03 Capabilities ── */}
            <section className="border-t border-[var(--line)]">
                <div className="mx-auto max-w-[1200px] px-6 sm:px-10 py-24 sm:py-32">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                        <div className="lg:col-span-5">
                            <div className="lg:sticky lg:top-28">
                                <SectionLabel n="03">Capabilities</SectionLabel>
                                <h2 className="lp-serif lp-h2 mt-6">Built for substantive legal work, not generic document automation.</h2>
                            </div>
                        </div>
                        <dl className="lg:col-span-7 border-t border-[var(--line)]">
                            {capabilities.map((c) => (
                                <div key={c.title} className="py-7 border-b border-[var(--line)] grid sm:grid-cols-[220px_1fr] gap-2 sm:gap-8">
                                    <dt className="text-[17px] font-medium leading-snug">{c.title}</dt>
                                    <dd className="lp-body">{c.description}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </section>

            {/* ── 04 Security ── */}
            <section id="security" className="border-t border-[var(--line)] scroll-mt-16">
                <div className="mx-auto max-w-[1200px] px-6 sm:px-10 py-24 sm:py-32">
                    <SectionLabel n="04">Security</SectionLabel>
                    <h2 className="lp-serif lp-h2 mt-6 max-w-[720px]">Your documents stay inside a private, US-only Google Cloud project.</h2>
                    <div className="mt-14 grid md:grid-cols-3 border-t border-[var(--line)]">
                        {security.map((s, i) => (
                            <div key={s.title} className={`py-8 md:pr-10 ${i > 0 ? "md:pl-10 md:border-l border-t md:border-t-0 border-[var(--line)]" : ""}`}>
                                <h3 className="text-[17px] font-medium leading-snug">{s.title}</h3>
                                <p className="lp-body mt-3">{s.description}</p>
                            </div>
                        ))}
                    </div>
                    <p className="lp-small mt-10 max-w-[720px]">
                        LoA is built by engineers who previously worked on systems at Meta, Google, Microsoft,
                        the U.S. Department of Defense, and public-sector judicial systems.
                    </p>
                </div>
            </section>

            {/* ── 05 FAQ ── */}
            <section id="faq" className="border-t border-[var(--line)] bg-[var(--paper-2)] scroll-mt-16">
                <div className="mx-auto max-w-[1200px] px-6 sm:px-10 py-24 sm:py-32">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                        <div className="lg:col-span-4">
                            <SectionLabel n="05">FAQ</SectionLabel>
                            <h2 className="lp-serif lp-h2 mt-6">Legal transcript analysis AI and document review, answered.</h2>
                        </div>
                        <div className="lp-faq lg:col-span-8 border-t border-[var(--line)]">
                            {faq.map((item) => (
                                <details key={item.q} className="border-b border-[var(--line)] group">
                                    <summary className="flex items-start justify-between gap-6 py-6 text-[17px] font-medium leading-snug">
                                        <span>{item.q}</span>
                                        <span className="lp-faq-icon shrink-0 mt-1 text-[var(--ink-3)]" aria-hidden>
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                                <path strokeLinecap="round" d="M12 5v14M5 12h14"/>
                                            </svg>
                                        </span>
                                    </summary>
                                    <p className="lp-body pb-7 max-w-[680px]">{item.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Contact ── */}
            <section id="contact" className="border-t border-[var(--line)] scroll-mt-16">
                <div className="mx-auto max-w-[1200px] px-6 sm:px-10 py-24 sm:py-32">
                    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                        <div className="lg:col-span-5">
                            <div className="lg:sticky lg:top-28">
                                <SectionLabel n="06">Contact</SectionLabel>
                                <h2 className="lp-serif lp-h2 mt-6">Talk to us about your matter.</h2>
                                <p className="lp-body mt-6 max-w-[440px]">
                                    Depositions, investigations, regulatory responses, or any review-heavy
                                    matter. Book a call or send an email and we&apos;ll reply the same day.
                                </p>
                                <a href={`mailto:${MAINTENANCE_CONTACT_EMAIL}`} className="lp-serif inline-block mt-8 text-[28px] sm:text-[34px] leading-none lp-link">
                                    {MAINTENANCE_CONTACT_EMAIL}
                                </a>
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <div className="rounded-[12px] border border-[var(--line)] bg-white overflow-hidden">
                                <iframe title="Book a call with LoA" src={CALENDLY_URL} className="w-full h-[700px]" frameBorder="0"/>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="border-t border-[var(--line)]">
                <div className="mx-auto max-w-[1200px] px-6 sm:px-10 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-[13px] text-[var(--ink-3)]">
                    <div className="flex items-center gap-4">
                        <Wordmark/>
                        <span>&copy; {new Date().getFullYear()}</span>
                    </div>
                    <div className="flex items-center gap-6">
                        <a href="#product" className="hover:text-[var(--ink)] transition">Product</a>
                        <a href="#security" className="hover:text-[var(--ink)] transition">Security</a>
                        <a href={`mailto:${MAINTENANCE_CONTACT_EMAIL}`} className="hover:text-[var(--ink)] transition">{MAINTENANCE_CONTACT_EMAIL}</a>
                    </div>
                </div>
            </footer>

            <AnimatePresence>
                {expandedDemo && <DemoLightbox demo={expandedDemo} onClose={() => setExpandedDemo(null)}/>}
            </AnimatePresence>
            <MaintenanceModal open={showMaintenance} onClose={closeMaintenance}/>
        </main>
    );
}
